import React, { useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Row,
	Select,
	Space,
	Steps,
	Table,
	Tag,
	Typography,
	Upload,
	message,
} from 'antd'
import {
	CheckCircleFilled,
	CloseCircleOutlined,
	CloudUploadOutlined,
	DownloadOutlined,
	FileTextOutlined,
	FolderOpenOutlined,
	InboxOutlined,
	TeamOutlined,
	UploadOutlined,
} from '@ant-design/icons'
import { departmentService } from '../../common/service'
import { staffService } from '../../common/service/staff.service'
import { httpPost } from '../../common/http.service'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { useThemeColors } from '../../common/ThemeContext'
import { useNavigate } from 'react-router-dom'

const { Title, Text, Paragraph } = Typography
const { Dragger } = Upload

const CSV_HEADERS = [
	'Staff_ID',
	'First_Name',
	'Last_Name',
	'Sex',
	'Name_In_English',
	'Bank_Account_Number',
	'Department',
	'Team',
	'Shift 1',
	'Shift 2',
	'Role',
	'E-mail',
	'Phone Number',
	'Status',
	'Night Shift-Emergency',
	'Ranking',
	'First_Line_Manager_ID',
	'Second_Line_Manager_ID',
	'Third_Line_Manager_ID',
	'Bio',
	'Experience',
	'Education',
] as const

const TEMPLATE_SAMPLE = {
	Staff_ID: '123',
	First_Name: 'Sok',
	Last_Name: 'Dara',
	Sex: 'male',
	Name_In_English: 'Dara Sok',
	Bank_Account_Number: '1234567890',
	Department: 'IT Department',
	Team: 'regular',
	'Shift 1': '08:00-12:00',
	'Shift 2': '13:00-17:00',
	Role: 'Officer',
	'E-mail': 'dara.sok@example.com',
	'Phone Number': '012345678',
	Status: 'active',
	'Night Shift-Emergency': '',
	Ranking: 'executor',
	First_Line_Manager_ID: '',
	Second_Line_Manager_ID: '',
	Third_Line_Manager_ID: '',
	Bio: '',
	Experience: '',
	Education: '',
}

const formatStaffIdForCsv = (id?: string) => String(id || '').replace(/^0+/, '')

const toShiftRange = (shift: any) => {
	const start = shift?.start || ''
	const end = shift?.end || ''
	return start && end ? `${start}-${end}` : ''
}

const escapeCsv = (value: unknown) => {
	const text = String(value ?? '')
	if (text.includes(',') || text.includes('"') || text.includes('\n')) {
		return `"${text.replace(/"/g, '""')}"`
	}
	return text
}

const rowsToCsv = (rows: Record<string, unknown>[]) => {
	const header = CSV_HEADERS.join(',')
	const body = rows.map((row) => CSV_HEADERS.map((h) => escapeCsv(row[h])).join(','))
	return [header, ...body].join('\n')
}

const downloadCsv = (fileName: string, csvText: string) => {
	const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

function UploadStaffData() {
	const navigate = useNavigate()
	const { primary } = useThemeColors()
	const [file, setFile] = useState<File | null>(null)
	const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([])
	const [previewLoading, setPreviewLoading] = useState(false)
	const [previewError, setPreviewError] = useState<string | null>(null)
	const [uploading, setUploading] = useState(false)
	const [exporting, setExporting] = useState(false)
	const [uploadConflicts, setUploadConflicts] = useState<
		Array<{ staffId?: string; type?: string; value?: string; staffIds?: string[]; message?: string }>
	>([])
	const [uploadErrorMsg, setUploadErrorMsg] = useState<string | null>(null)
	const [departmentOptions, setDepartmentOptions] = useState<
		Array<{ label: string; value: string }>
	>([])
	const [downloadDepartment, setDownloadDepartment] = useState<string>()

	const departmentNameById = useMemo(() => {
		const map = new Map<string, string>()
		for (const option of departmentOptions) {
			map.set(option.value, option.label)
		}
		return map
	}, [departmentOptions])

	useEffect(() => {
		const fetchDepartments = async () => {
			try {
				const res: any = await departmentService.fetchListing()
				if (res?.statusCode === 200) {
					setDepartmentOptions(
						(res.data || []).map((d: any) => ({
							label: d.name || d.id,
							value: d.id,
						})),
					)
				}
			} catch (error) {
				console.error('Cannot fetch departments for export', error)
			}
		}

		fetchDepartments()
	}, [])

	const parseCsvPreview = async (selectedFile: File) => {
		setPreviewLoading(true)
		setPreviewError(null)
		setPreviewRows([])

		try {
			const text = await selectedFile.text()
			const lines = text
				.split(/\r?\n/)
				.map((line) => line.trim())
				.filter(Boolean)

			if (lines.length === 0) {
				setPreviewError('CSV file is empty')
				return
			}

			const parseLine = (line: string) => {
				const cells: string[] = []
				let current = ''
				let inQuotes = false

				for (let i = 0; i < line.length; i++) {
					const ch = line[i]
					if (ch === '"') {
						if (inQuotes && line[i + 1] === '"') {
							current += '"'
							i++
						} else {
							inQuotes = !inQuotes
						}
					} else if (ch === ',' && !inQuotes) {
						cells.push(current)
						current = ''
					} else {
						current += ch
					}
				}

				cells.push(current)
				return cells.map((c) => c.trim())
			}

			const headers = parseLine(lines[0])
			if (headers.length === 0) {
				setPreviewError('Cannot read CSV headers')
				return
			}

			const dataRows = lines.slice(1).map(parseLine)
			const mappedRows = dataRows
				.filter((row) => row.some((cell) => cell !== ''))
				.slice(0, 20)
				.map((row, rowIndex) => {
					const obj: Record<string, string> = { __key: `${rowIndex}` }
					headers.forEach((header, index) => {
						obj[header || `Column_${index + 1}`] = row[index] || ''
					})
					return obj
				})

			setPreviewRows(mappedRows)
		} catch (error) {
			console.error('CSV preview parse error', error)
			setPreviewError('Cannot parse CSV preview. Please check file format.')
		} finally {
			setPreviewLoading(false)
		}
	}

	const extractSelectedFile = (info: any): File | null => {
		const fromCurrent = info?.file?.originFileObj || info?.file
		if (fromCurrent instanceof File) return fromCurrent

		const fromList = info?.fileList?.[0]?.originFileObj || info?.fileList?.[0]
		if (fromList instanceof File) return fromList

		return null
	}

	const handleBeforeUpload = (selectedFile: File) => {
		setFile(selectedFile)
		parseCsvPreview(selectedFile)
		return false
	}

	const handleFileChange = (info: any) => {
		const selectedFile = extractSelectedFile(info)
		setFile(selectedFile)
		if (selectedFile) {
			parseCsvPreview(selectedFile)
		} else {
			setPreviewRows([])
			setPreviewError(null)
		}
	}

	const handleUploadClick = async () => {
		if (!file) {
			message.warning('Please select a file to upload')
			return
		}

		const formData = new FormData()
		formData.append('file', file)

		setUploading(true)
		setUploadConflicts([])
		setUploadErrorMsg(null)

		try {
			await httpPost('api/v1/admin/upload-data', formData, {
				'Content-Type': 'multipart/form-data',
			})
			message.success('Your file was uploaded successfully!')
			setFile(null)
			setPreviewRows([])
			setPreviewError(null)
		} catch (error: any) {
			console.error('Bulk upload failed', error)
			const data = error?.response?.data
			const msg = data?.message
			const conflicts: any[] =
				data?.conflicts ??
				(typeof msg === 'object' && msg ? (msg as any).conflicts : undefined) ??
				[]
			const errorText =
				typeof msg === 'string'
					? msg
					: Array.isArray(conflicts) && conflicts.length > 0
					? 'Upload blocked due to duplicate email or phone conflicts. Review the staff IDs below and use Merge Staff to resolve them.'
					: 'Failed to upload file. Please try again.'
			setUploadErrorMsg(errorText)
			if (Array.isArray(conflicts) && conflicts.length > 0) {
				setUploadConflicts(conflicts)
			}
		} finally {
			setUploading(false)
		}
	}

	const buildCsvRowsFromStaff = (staffData: any[]) => {
		return (staffData || []).map((s) => ({
			Staff_ID: formatStaffIdForCsv(s.id),
			First_Name: s.firstName || '',
			Last_Name: s.lastName || '',
			Sex: s.sex || '',
			Name_In_English: s.nameInEnglish || '',
			Bank_Account_Number: s.bankAccountNumber || '',
			Department:
				departmentNameById.get(s.departmentId) ||
				s.department?.name ||
				s.departmentId ||
				'',
			Team: s.shift?.team || s.shift?.name || '',
			'Shift 1': toShiftRange(s.shift?.firstShift),
			'Shift 2': toShiftRange(s.shift?.secondShift),
			Role: s.role?.name || s.roleId || '',
			'E-mail': s.email || '',
			'Phone Number': s.phoneNumber || '',
			Status: s.status || '',
			'Night Shift-Emergency': s.isExtraShift ? 'ប្រចាំការពេលយប់' : '',
			Ranking: s.ranking || '',
			First_Line_Manager_ID: s.firstLineManager?.id || '',
			Second_Line_Manager_ID: s.secondLineManager?.id || '',
			Third_Line_Manager_ID: s.thirdLineManager?.id || '',
			Bio: s.bio || '',
			Experience: s.experience || '',
			Education: s.education || '',
		}))
	}

	const fetchAllStaff = async (departmentId?: string): Promise<any[]> => {
		const PAGE_SIZE = 500
		const allStaff: any[] = []
		let lastDocId: string | undefined

		while (true) {
			const params: Record<string, any> = { limit: PAGE_SIZE }
			if (departmentId) params.departmentId = departmentId
			if (lastDocId) params.lastId = lastDocId

			const res: any = await staffService.fetchListing(params)
			if (res?.statusCode !== 200) {
				throw new Error('Cannot load staff data for CSV export')
			}

			const page: any[] = Array.isArray(res.data) ? res.data : []
			allStaff.push(...page)

			if (!res.hasNextPage || page.length === 0) break
			lastDocId = res.lastDocId
		}

		return allStaff
	}

	const exportStaffCsv = async (departmentId?: string) => {
		setExporting(true)
		try {
			const staffData = await fetchAllStaff(departmentId)
			const rows = buildCsvRowsFromStaff(staffData)
			const csvText = rowsToCsv(rows)
			const dateText = new Date().toISOString().slice(0, 10)
			const fileName = departmentId
				? `staff_export_${departmentId}_${dateText}.csv`
				: `staff_export_all_${dateText}.csv`
			downloadCsv(fileName, csvText)
			message.success(
				departmentId
					? 'Department staff CSV downloaded successfully'
					: `All staff CSV downloaded (${staffData.length} records)`,
			)
		} catch (error) {
			console.error('CSV export failed', error)
			message.error('Failed to export CSV. Please try again.')
		} finally {
			setExporting(false)
		}
	}

	const downloadTemplate = () => {
		const csvText = rowsToCsv([TEMPLATE_SAMPLE])
		downloadCsv('staff_bulk_upload_template.csv', csvText)
		message.success('Bulk upload template downloaded')
	}

	const stepCurrent = file ? 1 : 0

	const previewColumns = useMemo(() => {
		if (!previewRows.length) return []
		return Object.keys(previewRows[0])
			.filter((key) => key !== '__key')
			.map((key) => ({
				title: key,
				dataIndex: key,
				key,
				width: 160,
				render: (value: string) => value || '-',
			}))
	}, [previewRows])

	return (
		<>
			<GlobalHeader />
			<div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
				{/* Page header */}
				<div style={{ marginBottom: 28 }}>
					<Title
						level={3}
						style={{ color: primary, margin: 0, fontFamily: 'Times New Roman' }}
					>
						<CloudUploadOutlined style={{ marginRight: 10 }} />
						Staff Bulk Upload
					</Title>
					<Paragraph type='secondary' style={{ margin: '6px 0 0' }}>
						Download a CSV template or your existing staff data, fill in the details,
						then upload back to the system. If the upload is blocked because email or
						phone is shared across staff, use{' '}
						<Button
							type='link'
							style={{ padding: 0, height: 'auto' }}
							onClick={() => navigate('/staff/merge')}
						>
							Merge staff
						</Button>{' '}
						first.
					</Paragraph>
				</div>

				{/* Progress steps */}
				<Steps
					current={stepCurrent}
					style={{ marginBottom: 32 }}
					items={[
						{
							title: 'Download CSV',
							description: 'Get your template or existing staff data',
						},
						{
							title: 'Upload CSV',
							description: 'Upload your modified CSV file',
						},
					]}
				/>

				{/* ─── Step 1 ─── */}
				<Card
					style={{ marginBottom: 24, borderRadius: 12, border: '1px solid #d6e4ff' }}
					styles={{ header: { background: '#f0f5ff', borderRadius: '12px 12px 0 0' } }}
					title={
						<Space>
							<Tag
								color={primary}
								style={{ fontSize: 13, padding: '2px 12px', borderRadius: 20 }}
							>
								Step 1
							</Tag>
							<Text strong style={{ fontSize: 15 }}>
								Download CSV Template
							</Text>
						</Space>
					}
				>
					<Row gutter={[16, 16]}>
						{/* Blank template */}
						<Col xs={24} sm={12} md={8}>
							<Card
								bordered
								style={{ height: '100%', borderColor: '#adc6ff', borderRadius: 8 }}
							>
								<Space direction='vertical' style={{ width: '100%' }} size={10}>
									<Space>
										<FileTextOutlined
											style={{ fontSize: 22, color: '#1677ff' }}
										/>
										<Text strong>Blank Template</Text>
									</Space>
									<Text type='secondary' style={{ fontSize: 13 }}>
										Download an empty CSV with all required column headers and a
										sample row to guide you.
									</Text>
									<Button
										icon={<DownloadOutlined />}
										onClick={downloadTemplate}
										block
										style={{ marginTop: 4 }}
									>
										Download Template
									</Button>
								</Space>
							</Card>
						</Col>

						{/* All staff */}
						<Col xs={24} sm={12} md={8}>
							<Card
								bordered
								style={{ height: '100%', borderColor: '#b7eb8f', borderRadius: 8 }}
							>
								<Space direction='vertical' style={{ width: '100%' }} size={10}>
									<Space>
										<TeamOutlined style={{ fontSize: 22, color: '#52c41a' }} />
										<Text strong>All Staff Data</Text>
									</Space>
									<Text type='secondary' style={{ fontSize: 13 }}>
										Export all current staff records in CSV format, ready for
										editing and re-upload.
									</Text>
									<Button
										icon={<DownloadOutlined />}
										onClick={() => exportStaffCsv()}
										loading={exporting}
										block
										style={{ marginTop: 4 }}
									>
										Download All Staff
									</Button>
								</Space>
							</Card>
						</Col>

						{/* By department */}
						<Col xs={24} sm={24} md={8}>
							<Card
								bordered
								style={{ height: '100%', borderColor: '#ffd591', borderRadius: 8 }}
							>
								<Space direction='vertical' style={{ width: '100%' }} size={10}>
									<Space>
										<FolderOpenOutlined
											style={{ fontSize: 22, color: '#fa8c16' }}
										/>
										<Text strong>By Department</Text>
									</Space>
									<Text type='secondary' style={{ fontSize: 13 }}>
										Export staff records filtered by a specific department.
									</Text>
									<Select
										allowClear
										showSearch
										style={{ width: '100%' }}
										placeholder='Select a department'
										value={downloadDepartment}
										onChange={(value) => setDownloadDepartment(value)}
										options={departmentOptions}
									/>
									<Button
										icon={<DownloadOutlined />}
										disabled={!downloadDepartment}
										loading={exporting}
										onClick={() => exportStaffCsv(downloadDepartment)}
										block
									>
										Download Department CSV
									</Button>
								</Space>
							</Card>
						</Col>
					</Row>
				</Card>

				{/* ─── Step 2 ─── */}
				<Card
					style={{ borderRadius: 12, border: '1px solid #d6e4ff' }}
					styles={{ header: { background: '#f0f5ff', borderRadius: '12px 12px 0 0' } }}
					title={
						<Space>
							<Tag
								color={file ? '#52c41a' : primary}
								style={{ fontSize: 13, padding: '2px 12px', borderRadius: 20 }}
							>
								Step 2
							</Tag>
							<Text strong style={{ fontSize: 15 }}>
								Upload Modified CSV
							</Text>
						</Space>
					}
				>
					{/* Drag & drop upload area */}
					<Dragger
						accept='.csv,.CSV'
						maxCount={1}
						showUploadList={false}
						beforeUpload={handleBeforeUpload}
						onChange={handleFileChange}
						style={{ borderRadius: 8 }}
					>
						<p className='ant-upload-drag-icon'>
							<InboxOutlined style={{ color: primary, fontSize: 48 }} />
						</p>
						<p className='ant-upload-text' style={{ fontWeight: 600 }}>
							Drag &amp; Drop your CSV file here
						</p>
						<p className='ant-upload-hint'>
							Supports .csv format only — or click to browse from your computer
						</p>
					</Dragger>

					{/* Selected file status */}
					{file && (
						<Alert
							style={{ marginTop: 16, borderRadius: 8 }}
							type='success'
							showIcon
							icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
							message={
								<Space style={{ width: '100%', justifyContent: 'space-between' }}>
									<Text strong>{file.name}</Text>
									<Button
										type='text'
										size='small'
										icon={
											<CloseCircleOutlined
												style={{ color: '#ff4d4f', fontSize: 16 }}
											/>
										}
										onClick={() => {
											setFile(null)
											setPreviewRows([])
											setPreviewError(null)
										}}
									/>
								</Space>
							}
						/>
					)}

					{/* CSV preview */}
					{file && (
						<div style={{ marginTop: 24 }}>
							<Divider orientation={'left' as any} orientationMargin={0}>
								<Text type='secondary' style={{ fontSize: 13 }}>
									CSV Preview
									{previewRows.length > 0 && (
										<Text
											type='secondary'
											style={{ fontSize: 12, marginLeft: 6 }}
										>
											({previewRows.length} row
											{previewRows.length !== 1 ? 's' : ''}
											loaded)
										</Text>
									)}
								</Text>
							</Divider>
							{previewError ? (
								<Alert
									type='error'
									message={previewError}
									showIcon
									style={{ borderRadius: 8 }}
								/>
							) : (
								<Table
									size='small'
									loading={previewLoading}
									rowKey='__key'
									columns={previewColumns as any}
									dataSource={previewRows}
									scroll={{ x: 'max-content' }}
									pagination={{
										pageSize: 5,
										showSizeChanger: false,
										size: 'small',
									}}
									style={{ borderRadius: 8, overflow: 'hidden' }}
								/>
							)}
						</div>
					)}

					{/* Upload action */}
					<Divider style={{ marginTop: file ? 24 : 16 }} />
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							gap: 12,
						}}
					>
						{!file && (
							<Text type='secondary' style={{ fontSize: 13 }}>
								Select a CSV file above to enable upload
							</Text>
						)}
						{file && (
							<Text type='secondary' style={{ fontSize: 13 }}>
								Ready to upload: <Text strong>{file.name}</Text>
							</Text>
						)}
						<Button
							type='primary'
							icon={<UploadOutlined />}
							loading={uploading}
							onClick={handleUploadClick}
							disabled={!file}
							size='large'
							style={{
								backgroundColor: primary,
								borderColor: primary,
								borderRadius: 8,
							}}
						>
							Upload Staff Data
						</Button>
					</div>

					{/* Upload error / conflict details */}
					{uploadErrorMsg && (
						<Alert
							type='error'
							showIcon
							style={{ marginTop: 16, borderRadius: 8 }}
							message={uploadErrorMsg}
							description={
								uploadConflicts.length > 0 ? (
									<div style={{ marginTop: 8 }}>
										<Text strong style={{ display: 'block', marginBottom: 6 }}>
											Conflicting staff:
										</Text>
										<ul style={{ margin: 0, paddingLeft: 20 }}>
											{uploadConflicts.map((c, i) => (
												<li key={i}>
													{c.staffId ? (
														<>
															<Text code>{c.staffId}</Text>
															{c.message ? ` — ${c.message}` : ''}
														</>
													) : (
														<>
															<Tag color='volcano'>{c.type}</Tag>
															<Text code>{c.value}</Text>
															{' — Staff IDs: '}
															{(c.staffIds || []).map((sid) => (
																<Text key={sid} code style={{ marginRight: 4 }}>
																	{sid}
																</Text>
															))}
														</>
													)}
												</li>
											))}
										</ul>
										<Button
											type='link'
											style={{ padding: 0, marginTop: 8, height: 'auto' }}
											onClick={() => navigate('/staff/merge')}
										>
											Go to Merge Staff →
										</Button>
									</div>
								) : undefined
							}
							closable
							onClose={() => {
								setUploadErrorMsg(null)
								setUploadConflicts([])
							}}
						/>
					)}
				</Card>
			</div>
		</>
	)
}

export default UploadStaffData
